import { SubscriptionListModal } from "@/components/editor/stripe/SubscriptionListModal";
import { MODAL_NAME } from "@/components/editor/stripe/constants";
import { Modal } from "antd";
import axios from "axios";
import { MutableRefObject, useCallback, useEffect } from "react";
import { useTranslation } from "next-i18next";
import { NoticeModal } from "./NoticeModal";
import { useAppDispatch, useAppSelector } from "@/hooks/redux";
import { projectActions } from "@/redux/stores/projectStore";
import { LoginModal } from "./LoginModal";
import { getDashBoardState, getProjectState } from "@/redux/store";
import { useRouter } from "next/router";
import { dashboardActions } from "@/redux/stores/dashboardStore";
import { useGetEnvQuery } from "@/redux/api/commonApi";
import { PlanSwitchModal } from "./PlanSwitchModal";
import MobileSubscriptionModal from "@/components/common/modal/MobileSubscriptionModal";
import { usePurchasePopup } from "@/components/common/purchasePopup/PurchasePopupContext";
import { PURCHASE_TYPE } from "@/components/common/purchasePopup/constants";
import { useSubscription } from "@/hooks/useSubscription";
import { ApiSubscriptionListModal } from "./ApiSubscriptionListModal";


interface StripeModalType {
  successRedirect? : string,
  stripeFuncRef? : MutableRefObject<any>
}

export const StripeModal = (props: StripeModalType) => {
  const { t } = useTranslation();
  const router = useRouter();
  const { accountUserInfo } = useAppSelector((store) => getDashBoardState(store) ?? false );
  const { data: studioUrl } = useGetEnvQuery(
    { key: "NEXTAUTH_URL" },
    {
      pollingInterval: 30 * 60 * 1000,
      refetchOnFocus: false,
      refetchOnMountOrArgChange: false,
      refetchOnReconnect: false,
      skip: false,
    }
  );

  const clientSecret = useAppSelector(
    (store) => getProjectState(store).clientSecret
  );

  const openMobileSubscriptionModal = useAppSelector(
    (store) => getProjectState(store).openMobileSubscriptionModal
  );

  const { setOpenedModal } = projectActions;
  const dispatch = useAppDispatch();

  useEffect(() => {
    if (clientSecret) {
      dispatch(setOpenedModal(MODAL_NAME.CHECKOUT));
    }
  }, [clientSecret, setOpenedModal, dispatch]);

  const startSubscription = useCallback(
    async (plan: string, period: boolean, seats?: number) => {  //plan: "personal" | "team"
      try {

        await axios.post("/api/user/referral", {});

        const periodKey = period ? "monthly" : "yearly";
        const apiUrl = "/api/user/subscriptions";
        const uriWithoutQueryParam = router?.asPath.split("?")[0];
        const uriWithoutQueryParamSucess = props.successRedirect || router?.asPath.split("?")[0];

        const cancelUrl = `${studioUrl}` + uriWithoutQueryParam;
        const successUrl = `${studioUrl}/checkout_redirect?session_id={CHECKOUT_SESSION_ID}&redirect=${uriWithoutQueryParamSucess}`;
        const couponItem = sessionStorage.getItem("coupon") ?? "";
 
        if (window?.dataLayer?.push) {
          window.dataLayer.push({
            event: "begin_checkout",
            plan_period: periodKey,
            plan_name: plan,
            plan_detail: plan,
            plan_subscription: "new",
            user_id: accountUserInfo?.id,
          });
        }

        window.gtag('set', 'user_properties', {
          'purchase_plan_period': periodKey,
          'purchase_plan_name': plan,
          'purchase_plan_detail': plan,
        });

        const subscriptionRes = await axios.post(apiUrl, {
          product: "studio3.0",
          plan,
          seats: seats ?? 1,
          periodKey,
          successUrl,
          cancelUrl,
          coupon: couponItem,
        });

        if (subscriptionRes.data.success) {
          router?.replace(subscriptionRes.data.data);
        } else {
          Modal.error({ content: subscriptionRes.data.data.msg });
        }
      } catch (error) {
        console.log("error: ", error);
      }
    },
    [props.successRedirect, router, studioUrl, accountUserInfo.id]
  );

  const {
    open: openPurchasePopup,
  } = usePurchasePopup()

  const {
    plan: currenPlan
  } = useSubscription()

  const currentPlanLevel = currenPlan?.level.current;
  const isPaidPlan = currentPlanLevel === 1 || currentPlanLevel === 2
  const isUnlimitedPlan = isPaidPlan && !!currenPlan?.unlimited

  const upgradeSubscription = useCallback(
    async (plan: string, period: boolean, seats?: number) => {
      try {
        const isMaster = accountUserInfo?.company?.isMaster ?? false;
        if(!isMaster) return Modal.error({ content: t("Only master account can change subscription status.") });
        const periodKey = period ? "monthly" : "yearly";
        const url = `/api/user/subscriptions/upgrade`;
        const uriWithoutQueryParam = router?.asPath.split("?")[0];
        const successUrl = `${studioUrl}/checkout_redirect?isUpgrade=true&redirect=${uriWithoutQueryParam}`;

        if (window?.dataLayer?.push) {
          window.dataLayer.push({
            event: "begin_checkout",
            plan_period: periodKey,
            plan_name: plan,
            plan_detail: plan,
            plan_subscription: "upgrade",
            user_id: accountUserInfo?.id,
          });
        }

        window.gtag('set', 'user_properties', {
          'purchase_plan_period': periodKey,
          'purchase_plan_name': plan,
          'purchase_plan_detail': plan,
        });

        const isApiPlan = ["standard", "premium"].includes(plan);
        const isSubscriptionPlan = ["personal", "team"].includes(plan);

        /**
         * API 요금제를 구매하려고 하거나\
         * 이미 유료 무제한 요금제인 경우\
         * 결제 팝업을 표시한다.
         */
        if (isApiPlan || (isPaidPlan && isUnlimitedPlan && isSubscriptionPlan)) {
          /**
           * 유료 무제한 요금제인 경우 업그레이드 팝업 띄우기
           */
          if (isSubscriptionPlan) {
            openPurchasePopup?.({
              type: PURCHASE_TYPE.SUBSCRIPTION,
              plan: plan as "personal" | "team",
              period: periodKey,
              seats,
            })
          }
          else if (isApiPlan) {
            openPurchasePopup?.({
              type: PURCHASE_TYPE.API_SUBSCRIPTION,
              plan: plan as "standard" | "premium",
              period: periodKey,
            })
          }

          dispatch(setOpenedModal(MODAL_NAME.NONE));

          return;
        }

        /**
         * API 요금제를 구매하려는 것이 아니고\
         * 무제한 요금제가 아닌 경우 (구 요금제) 기존 기능 그대로 진행
         * 
         * 하지만 정상적인 경우라면 구 요금제는 여기에 들어오지 않는다
         */

        const subscriptionRes = await axios.post(url, {
          plan,
          periodKey,
          seats: seats ?? 1,
          successUrl,
        });

        if (subscriptionRes.data.success) {
          router?.replace(subscriptionRes.data.data);
        } else {
          Modal.error({ content: subscriptionRes.data.data.msg });
          console.log("error subscriptionRes: ", subscriptionRes.data);

          if (window?.dataLayer?.push) {
            window.dataLayer.push({
              event: "exception",
              error_type: "payment_upgrade",
              error_message: subscriptionRes.data.data.msg,
            });
          }
        }
      } catch (error) {
        console.log("error: ", error);
        if (window?.dataLayer?.push) {
          window.dataLayer.push({
            event: "exception",
            error_type: "payment_upgrade",
            error_message: error,
          });
        }
      }
    },
    [
      router,
      studioUrl,
      accountUserInfo.id,
      openPurchasePopup,
      isPaidPlan,
      isUnlimitedPlan,
      accountUserInfo?.company?.isMaster,
      dispatch,
      setOpenedModal,
      t,
    ],
  );

  const downgradeSubscription = useCallback(
    (plan: string, open: boolean) => {
      console.log("downgrade");
      dispatch(
        dashboardActions.setShowDowngradeModal({ type: plan, value: open })
      );
    },
    [dispatch]
  );

  const subscriptionModalProps = {
    startSubscription,
    upgradeSubscription,
    downgradeSubscription,
  };

  useEffect(()=>{
    if (props.stripeFuncRef) {
      props.stripeFuncRef.current = {startSubscription}
    }
  }, [props.stripeFuncRef, startSubscription])

  const noticeModalProps = {
    cancelButtonText: "cancel",
    okButtonText: "Upgrade",
  };

  const setOpenMobileSubscriptionModal = useCallback((open: boolean) => {
    dispatch(projectActions.setOpenMobileSubscriptionModal(open));
  }, [dispatch]);

  return (
    <div>
      <LoginModal />
      <NoticeModal {...noticeModalProps} />
      <SubscriptionListModal {...subscriptionModalProps} />
      <ApiSubscriptionListModal {...subscriptionModalProps}/>
      <PlanSwitchModal />
      <MobileSubscriptionModal
        modalOpen={openMobileSubscriptionModal}
        setModalOpen={setOpenMobileSubscriptionModal}
      />
    </div>
  );
};
